import React from 'react';
import PropTypes from 'prop-types';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Select, { components } from 'react-select';
import axios from 'axios';
import { getPutPostConfig } from '../ResourceApi';
import { userMinimalShape } from '../propTypes';

export function StatusIcon({ status, action }) {
  return <div role="button" tabIndex={-1} className={`status-icon ${status}`} onClick={action} />;
}

StatusIcon.propTypes = {
  status: PropTypes.string.isRequired,
  action: PropTypes.func,
};

StatusIcon.defaultProps = {
  action: undefined,
};

export function LoadingAnimation({ loading, inline }) {
  if (!loading) {
    return '';
  }

  if (typeof loading === 'string') {
    return (
      <Alert className="mb-4" variant="danger">
        <p className="mb-0">{loading}</p>
      </Alert>
    );
  }

  return (
    <div className={`loading ${inline ? 'inline' : ''}`}>
      <Spinner animation="border" role="status" variant="info" />
    </div>
  );
}

LoadingAnimation.propTypes = {
  loading: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  inline: PropTypes.bool,
};

LoadingAnimation.defaultProps = {
  loading: false,
  inline: false,
};

export function Option(props) {
  return (
    <div className={`text-${props.data.className}`}>
      <components.Option {...props} />
    </div>
  );
}

Option.propTypes = {
  data: PropTypes.shape({ className: PropTypes.string }),
};

Option.defaultProps = {
  data: { className: '' },
};

export function SingleValue(props) {
  const className = `text-${props.data.className} project-status-select small-text`;

  return (
    <components.SingleValue {...props} className={className} />
  );
}

SingleValue.propTypes = {
  data: PropTypes.shape({ className: PropTypes.string }),
};

SingleValue.defaultProps = {
  data: { className: '' },
};

export function ProjectStatusSelector({
  onlyNewOptions = false,
  readOnly = false,
  projectId,
  value: currentValue,
}) { // eslint-disable-line react/prop-types, max-len, max-len
  const newOptions = [
    { label: '', value: 28, className: 'secondary' },
    { label: 'Identify the Problem', value: 22, className: 'secondary' },
    { label: 'Technical Engagement', value: 23, className: 'secondary' },
    { label: 'Define the Solution', value: 24, className: 'warning' },
    { label: 'Build the Backlog', value: 25, className: 'warning' },
    { label: 'Delivery', value: 26, className: 'info' },
    { label: 'Done', value: 27, className: 'success' },
  ];

  const oldOptions = [
    { label: 'New Concept', value: 15, className: 'secondary' },
    { label: 'Stakeholder - Requirements Gathering', value: 16, className: 'warning' },
    { label: 'Stakeholder - Documenting Requirements', value: 17, className: 'warning' },
    { label: 'Stakeholder - On Hold', value: 18, className: 'danger' },
    { label: 'Awaiting Resource', value: 21, className: 'danger' },
    { label: 'Fully Briefed', value: 1, className: 'warning' },
    { label: 'In Planning', value: 2, className: 'success' },
    { label: 'In Analysis', value: 3, className: 'success' },
    { label: 'Ready for Development', value: 19, className: 'warning' },
    { label: 'In Development', value: 5, className: 'success' },
    { label: 'Ready for Testing', value: 6, className: 'warning' },
    { label: 'In Testing', value: 7, className: 'success' },
    { label: 'Live - Ongoing Development', value: 9, className: 'info' },
    { label: 'Live - Complete', value: 20, className: 'info' },
    { label: 'On Hold', value: 11, className: 'danger' },
  ];

  let value;

  const options = [...newOptions, ...oldOptions];

  options.forEach((item) => {
    if (item.value === currentValue) {
      value = item;
    }
  });

  const onChange = (data) => {
    axios.post(`/projects/${projectId}`, {
      _method: 'PUT',
      project_status_id: data.value,
    }, getPutPostConfig());
  };

  if (readOnly) {
    return (
      <div className={`text-${value?.className} project-status-select`}>
        {value?.label}
      </div>
    );
  }

  let availableOptions = options;
  if (onlyNewOptions) {
    availableOptions = newOptions;

    // If the current status is not in the new options, add it to the list
    if (!availableOptions.find((option) => option.value === currentValue)) {
      availableOptions.push(value);
    }
  }

  return (
    <Select
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 1071 }) }}
      menuPortalTarget={document.body}
      onChange={onChange}
      options={availableOptions}
      components={{ Option, SingleValue }}
      defaultValue={value}
    />
  );
}

ProjectStatusSelector.propTypes = {
  onlyNewOptions: PropTypes.bool,
  readOnly: PropTypes.bool,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  value: PropTypes.number,
};

ProjectStatusSelector.defaultProps = {
  onlyNewOptions: false,
  readOnly: false,
  value: undefined,
};

export function RagStatusSelect({ value, readOnly, action }) {
  const options = [
    { label: 'None', value: 'none', className: 'secondary' },
    { label: 'Red', value: 'red', className: 'danger' },
    { label: 'Amber', value: 'amber', className: 'warning' },
    { label: 'Green', value: 'green', className: 'success' },
  ];

  let selected;

  options.forEach((item) => {
    if (item.value === value) {
      selected = item;
    }
  });

  if (readOnly) {
    return (
      <div className={`text-${selected.className} rag-status`}>
        {selected.label}
      </div>
    );
  }

  return (
    <Select
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 1051 }) }}
      menuPortalTarget={document.body}
      onChange={action}
      options={options}
      components={{ Option, SingleValue }}
      value={selected}
    />
  );
}

RagStatusSelect.propTypes = {
  value: PropTypes.string,
  readOnly: PropTypes.bool,
  action: PropTypes.func,
};

RagStatusSelect.defaultProps = {
  value: 'none',
  readOnly: false,
  action: undefined,
};

export function UserSelect({ users, value, onChange }) {
  const options = [];
  let selected;

  users.forEach((user) => {
    options.push({ label: user.name, value: user.id });
  });

  options.forEach((item) => {
    if (item.value === value) {
      selected = item;
    }
  });

  return (
    <Select onChange={onChange} options={options} value={selected} />
  );
}

UserSelect.propTypes = {
  users: PropTypes.arrayOf(userMinimalShape).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
};

UserSelect.defaultProps = {
  value: undefined,
  onChange: undefined,
};

export function ReportSelect({ value, onChange }) {
  const options = [
    { label: 'Project List', value: '/reports/project/list' },
    { label: 'Project Overview', value: '/reports/project/overview' },
    { label: 'Time by Project', value: '/reports/time/projects' },
    { label: 'Time by Squad', value: '/reports/time/squad-overview' },
    { label: 'Time by Role', value: '/reports/time/group-overview' },
    { label: 'Time by User', value: '/reports/time/users' },
    { label: 'Time by Day', value: '/reports/time/by-day' },
    { label: 'Meeting Time', value: '/reports/time/meetings' },
    { label: 'Unapproved Time', value: '/reports/time/unapproved' },
    { label: 'All Time Logged', value: '/reports/time/all' },
    { label: 'Proportional Time: Accounted by Person', value: '/reports/time/working-hours' },
    { label: 'Proportional Time: By Person', value: '/reports/time/proportional' },
    { label: 'Proportional Time: By Project', value: '/reports/time/project-proportional' },
  ];

  if (window.props.activeUser.isInGroup(window.props.groups.CAN_VIEW_EMPLOYEE_SALARIES)) {
    options.push({ label: 'Export Time Data for BigQuery', value: '/reports/time/time-log-export' });
  }

  let selected;

  options.forEach((item) => {
    if (item.value === value) {
      selected = item;
    }
  });

  return (
    <Select onChange={onChange} options={options} value={selected} />
  );
}

ReportSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
};

ReportSelect.defaultProps = {
  value: undefined,
  onChange: undefined,
};

export function Icon({
  icon,
  size = false,
  fixed = false,
  variant = 'fa',
  append = '',
  onClick = null,
  style = null,
  title = null,
}) {
  let className = `${variant} fa-${icon} ${append}`;

  if (size) {
    className += ` fa-${size}`;
  }

  if (fixed) {
    className += ' fa-fw';
  }

  return <i title={title} role="button" tabIndex={-1} className={className} onClick={onClick} style={style} />;
}

Icon.propTypes = {
  icon: PropTypes.string.isRequired,
  size: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  fixed: PropTypes.bool,
  variant: PropTypes.string,
  append: PropTypes.string,
  onClick: PropTypes.func,
  style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  title: PropTypes.string,
};

Icon.defaultProps = {
  size: false,
  fixed: false,
  variant: 'fa',
  append: '',
  onClick: null,
  style: null,
  title: null,
};

export function Duration({ minutes }) {
  let display;
  const hours = minutes / 60;

  if (minutes === null || minutes === 0) {
    display = '-';
  } else if (minutes < 60) {
    display = `${minutes}m`;
  } else {
    display = `${Math.round(hours)}h`;

    if (hours > 7.7) {
      display = `${Math.floor(hours / 7.7)}d ${Math.round(hours % 7.7)}h`;
    }
  }

  return (
    <span>{display}</span>
  );
}

Duration.propTypes = {
  minutes: PropTypes.number,
};

Duration.defaultProps = {
  minutes: 0,
};
